import { BooheeFoodLookup } from '@/infrastructure/food/BooheeFoodLookup'
import { CloudFoodPhotoRecognizer } from '@/infrastructure/food/CloudFoodPhotoRecognizer'
import { OpenFoodFactsLookup } from '@/infrastructure/food/OpenFoodFactsLookup'
import { useEffect, useState } from 'react'
import { RouterProvider } from 'react-router-dom'

import { HealthRepository } from '@/domain/health/HealthRepository'
import { CareBabyDb } from '@/infrastructure/db/CareBabyDb'
import { ShortcutSyncProvider } from '@/infrastructure/db/ShortcutSyncProvider'
import { DexieManualHealthStore } from '@/infrastructure/db/DexieManualHealthStore'
import { BackupService } from '@/infrastructure/backup/BackupService'
import { UpdatePrompt } from '@/shared/components/UpdatePrompt'

import type { AppServices } from './AppProviders'
import { createAppRouter } from './router'

export interface AppRuntime {
  router: ReturnType<typeof createAppRouter>
  close(): void
}

export type AppRuntimeFactory = () => AppRuntime

interface AppProps {
  runtimeFactory?: AppRuntimeFactory
  showUpdatePrompt?: boolean
}

function createProductionRuntime(): AppRuntime {
  const db = new CareBabyDb('carebaby')
  const manualStore = new DexieManualHealthStore(db)
  const sync = new ShortcutSyncProvider()
  const foodLookup = new OpenFoodFactsLookup()
  const foodPhoto = new CloudFoodPhotoRecognizer((image, signal) => sync.requestFoodPhoto(image, signal))
  const chineseFood = new BooheeFoodLookup((body, signal) => sync.requestFoodLookup(body, signal))
  const repository = new HealthRepository([manualStore], manualStore, foodLookup, foodPhoto, chineseFood)
  const backupService = new BackupService(manualStore)
  const services: AppServices = { repository, manualStore, backupService, sync, foodLookup, foodPhoto, chineseFood }
  return {
    router: createAppRouter(services),
    close: () => { sync.close(); db.close() },
  }
}

function BootShell() {
  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">跳到主要内容</a>
      <header className="app-header">
        <span className="app-brand">CareBaby</span>
        <a className="settings-link" href="/settings">设置</a>
      </header>
      <main id="main-content" tabIndex={-1}>
        <h1>今日状态</h1>
      </main>
      <nav className="bottom-tabs" aria-label="主要导航">
        <a href="/" aria-current="page">今日状态</a>
        <a href="/workouts">健身记录</a>
      </nav>
    </div>
  )
}

export function App({ runtimeFactory = createProductionRuntime, showUpdatePrompt = true }: AppProps) {
  const [runtime, setRuntime] = useState<AppRuntime | null>(null)

  useEffect(() => {
    const nextRuntime = runtimeFactory()
    // Runtime acquisition belongs to effect lifecycle so StrictMode can dispose/recreate it safely.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRuntime(nextRuntime)
    return () => {
      nextRuntime.close()
    }
  }, [runtimeFactory])

  return (
    <>
      {showUpdatePrompt ? <UpdatePrompt /> : null}
      {runtime === null ? <BootShell /> : <RouterProvider router={runtime.router} />}
    </>
  )
}
